import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  TextField,
  Autocomplete,
} from "@mui/material";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import {
  useLazyListCODReportQuery,
  useDownloadCODReportExcelMutation,
} from "store/services/reportApi";
import { useLazyGetDDLVendorListReportQuery } from "store/services/reportApi";
import HighlightedText from "components/Shared/HighlightedText";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { Download } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { differenceInDays } from "date-fns";

const getRemitStatusStyle = (status) => {
  const styles = {
    Transferred: { backgroundColor: "#DFF7DF", textColor: "green" },
    "Not Transferred": { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[status] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const CODReport = () => {
  const { isVendor, vendorId, viewEntity } = useAuth();
  const { reset } = useForm({
    defaultValues: { selectedPrices: [] },
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  // FIX: Remove local keyword state and read directly from searchParams
  const keyword = searchParams.get("search") || "";
  const [sortBy, setSortBy] = useState("createdAt:-1");
  const [timeRange, setTimeRange] = useState("today");
  const [remitStatus, setRemitStatus] = useState("Not Transferred");
  const [vendorIdFilter, setVendorIdFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [customFromDate, setCustomFromDate] = useState(null);
  const [customToDate, setCustomToDate] = useState(null);
  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "COD Transfer Report" },
  ];

  const [fetchCODReport, { data: codRes, isLoading, isFetching, error }] =
    useLazyListCODReportQuery();
  const [
    fetchVendors,
    { data: vendorData, isLoading: loadingVendors, error: vendorError },
  ] = useLazyGetDDLVendorListReportQuery();
  const [downloadExcel, { isLoading: isDownloading }] =
    useDownloadCODReportExcelMutation();

  useEffect(() => {
    console.log("viewEntity:", viewEntity);
  }, [viewEntity]);

  const getDateRange = (range, from, to) => {
    const today = moment().startOf("day");
    switch (range) {
      case "today":
        return {
          fromDate: today.format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "last7days":
        return {
          fromDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "oneMonth":
        return {
          fromDate: moment()
            .subtract(1, "month")
            .startOf("day")
            .format("MM-DD-YYYY"),
          toDate: today.endOf("day").format("MM-DD-YYYY"),
        };
      case "custom":
        if (from && to) {
          const diff = differenceInDays(new Date(to), new Date(from));
          if (diff > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { fromDate: null, toDate: null };
          }
          if (diff < 0) {
            setErrorMessage("To Date cannot be before From Date.");
            return { fromDate: null, toDate: null };
          }
          return {
            fromDate: moment(from).format("MM-DD-YYYY"),
            toDate: moment(to).format("MM-DD-YYYY"),
          };
        }
        setErrorMessage("Please select both From and To dates.");
        return { fromDate: null, toDate: null };
      case "all":
      default:
        return { fromDate: null, toDate: null };
    }
  };
  const handleSearchChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("search", value);
    } else {
      newParams.delete("search");
    }
    // Reset to page 1 when searching
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (viewEntity?.entity?._id) {
      fetchVendors({
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        status: "Active",
      });
    }
  }, [fetchVendors, viewEntity]);

  useEffect(() => {
    if (vendorError) {
      setErrorMessage(vendorError?.data?.message || "Failed to load vendors");
    }
  }, [vendorError]);

  useEffect(() => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    const newParams = new URLSearchParams(searchParams);
    if (fromDate) newParams.set("fromDate", fromDate);
    else newParams.delete("fromDate");
    if (toDate) newParams.set("toDate", toDate);
    else newParams.delete("toDate");
    newParams.set("remitStatus", remitStatus);
    if (vendorIdFilter) newParams.set("vendorId", vendorIdFilter);
    else newParams.delete("vendorId");
    if (keyword) newParams.set("search", keyword.replace(/[-]/g, ""));
    else newParams.delete("search");
    setSearchParams(newParams, { replace: true });

    const apiParams = {
      chargeType: "Cash on Delivery",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword: keyword.replace(/[-]/g, "") }),
      ...(isVendor && { vendorId }),
      ...(vendorIdFilter && { vendorId: vendorIdFilter }),
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      sortedBy,
      sortDirection,
      ...(remitStatus !== "all" && { remitStatus }),
    };

    console.log("API parameters:", apiParams);
    fetchCODReport(apiParams);
  }, [
    fetchCODReport,
    currentPage,
    perPage,
    keyword,
    isVendor,
    vendorId,
    vendorIdFilter,
    viewEntity,
    timeRange,
    sortBy,
    remitStatus,
    customFromDate,
    customToDate,
    setSearchParams,
    searchParams,
  ]);

  const clearAllFilters = () => {
    setSearchParams({ remitStatus: "all" });
    setTimeRange("all");
    setRemitStatus("all");
    setVendorIdFilter("");
    setCustomFromDate(null);
    setCustomToDate(null);
    reset({ selectedPrices: [] });
  };

  const handleDownloadExcel = async () => {
    const [sortedBy, sortDirection] = sortBy.split(":");
    const { fromDate, toDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    try {
      const response = await downloadExcel({
        chargeType: "Cash on Delivery",
        ...(keyword && { keyword: keyword.replace(/[-]/g, "") }),
        ...(isVendor && { vendorId }),
        ...(vendorIdFilter && { vendorId: vendorIdFilter }),
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,
        ...(fromDate && { fromDate }),
        ...(toDate && { toDate }),
        sortedBy,
        sortDirection,
        ...(remitStatus !== "all" && { remitStatus }),
      }).unwrap();
      console.log("Download response:", response);

      const url = window.URL.createObjectURL(response.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `COD_Report_${moment().format("YYYYMMDD")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      setErrorMessage(error?.data?.message || "Failed to download Excel file");
    }
  };

  const columns =  [
    {
      name: "Shipment ID",
      selector: (row) => row.shipment?.shipmentId,
      width: "170px",
      cell: (row) => {
        const shipmentIdRaw = row?.shipment?.shipmentId;
        const shipmentDbId = row?.shipment?._id;

        const formattedId = shipmentIdRaw
          ?.replace(/(.{4})/g, "$1-")
          .slice(0, -1);

        return shipmentDbId ? (
          <Link
            to={`/app/crm/shipment/${shipmentDbId}/detail?tab=pricing`}
            title={shipmentIdRaw}
            className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
          >
            {shipmentIdRaw ? (
              <HighlightedText text={formattedId} highlight={keyword} />
            ) : (
              "N/A"
            )}
          </Link>
        ) : (
          "N/A"
        );
      },
    },
    {
      name: "Fee Item",
      selector: (row) => row.feeItem?.name,
      cell: (row) => row.feeItem?.name || "N/A",
    },
    // {
    //   name: "COD Amount",
    //   selector: (row) => row.charge,
    //   cell: (row) => `Rs. ${row.charge?.toFixed(2) || "0.00"}`,
    // },
    {
      name: "Transfer Amount",
      selector: (row) => row.transferAmount,
      cell: (row) => `Rs. ${row.transferAmount?.toFixed(2) || "0.00"}`,
    },
    {
      name: "Payer",
      selector: (row) => row.payer,
      cell: (row) => row.payer || "N/A",
    },
    {
      name: "Payment Location",
      selector: (row) => row.paymentLocation,
      cell: (row) => row.paymentLocation || "N/A",
    },
    {
      name: "Vendor",
      selector: (row) => row.shipment?.sender?.vendorOwnerName,
      cell: (row) => (
        <div className="text-center">
          <div>{row.shipment?.sender?.vendorOwnerName || "Unknown Owner"}</div>
          <div className="text-xs text-gray-500 dark:text-gray-300">
            {row.shipment?.sender?.shopName || "Unknown Shop"}
          </div>
        </div>
      ),
    },
    {
      name: "Remit Status",
      selector: (row) => row.remitStatus,
      cell: (row) => (
        <Chip
          label={row?.remitStatus || "N/A"}
          variant="outlined"
          sx={{
            backgroundColor: getRemitStatusStyle(row?.remitStatus)
              .backgroundColor,
            color: getRemitStatusStyle(row?.remitStatus).textColor,
            fontWeight: "bold",
            height: "17px",
            borderRadius: "12px",
            width: "auto",
            padding: "0",
            border: "none",
            "& .MuiChip-label": {
              fontSize: "0.6rem",
              padding: "3px 5px",
              lineHeight: "14px",
            },
          }}
        />
      ),
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row.createdAt} />,
    },

    {
      name: "Transfer Date",
      selector: (row) => row.codCreatedAt,
      cell: (row) => <AppTableDateTime date={row.codCreatedAt} />,
    },
  ];

  const vendorOptions = Array.isArray(vendorData?.data)
    ? vendorData.data.map((v) => ({
      value: v.value,
      label: v.label,
    }))
    : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <BreadcrumbLayout breadcrumbs={breadcrumbs}>
        <BreadcrumbLayout.Error error={error || vendorError} />
        <BreadcrumbLayout.Paper>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">COD Transfer Report</Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <BaseSelect
                    label="Time Range"
                    value={timeRange}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      if (e.target.value !== "custom") {
                        setCustomFromDate(null);
                        setCustomToDate(null);
                      }
                    }}
                    options={[
                      { label: "Today", value: "today" },
                      { label: "Last 7 Days", value: "last7days" },
                      { label: "One Month", value: "oneMonth" },
                      { label: "Custom", value: "custom" },
                      { label: "All", value: "all" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                    size="small"
                    sx={{ minWidth: 160 }}
                  />
                  {timeRange === "custom" && (
                    <Stack direction="row" spacing={1}>
                      <DatePicker
                        label="From Date"
                        value={customFromDate}
                        onChange={(newValue) => setCustomFromDate(newValue)}
                        maxDate={customToDate || new Date()}
                        disableFuture
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 160 }}
                          />
                        )}
                      />
                      <DatePicker
                        label="To Date"
                        value={customToDate}
                        onChange={(newValue) => setCustomToDate(newValue)}
                        minDate={customFromDate}
                        maxDate={new Date()}
                        disableFuture
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ width: 160 }}
                          />
                        )}
                      />
                    </Stack>
                  )}
                  <Autocomplete
                    size="small"
                    options={[
                      { value: "", label: "All Vendors" },
                      ...vendorOptions,
                    ]}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value
                    }
                    value={
                      vendorOptions.find(
                        (opt) => opt.value === vendorIdFilter
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      setVendorIdFilter(newValue?.value || "")
                    }
                    disableClearable
                    disabled={loadingVendors || isVendor}
                    sx={{ minWidth: 200 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Vendor" />
                    )}
                  />
                </Box>
                <TableFilter
                  searchLabel="Shipment ID"
                  disabled={isLoading || isFetching || loadingVendors}
                  initialFilters={remitStatus !== "all" ? 1 : 0}
                  onClear={clearAllFilters}
                  sx={{ minWidth: 200 }}
                  showDateRange={false}
                  onSearchChange={handleSearchChange}
                  searchValue={keyword}
                >
                  <BaseSelect
                    fullWidth
                    label="Remit Status"
                    value={remitStatus}
                    onChange={(e) => setRemitStatus(e.target.value)}
                    sx={{ maxWidth: 200 }}
                    options={[
                      { label: "All", value: "all" },
                      { label: "Transferred", value: "Transferred" },
                      { label: "Not Transferred", value: "Not Transferred" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                  />
                  <BaseSelect
                    fullWidth
                    label="Sort By"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    options={[
                      { label: "Created At (Desc)", value: "createdAt:-1" },
                      { label: "Created At (Asc)", value: "createdAt:1" },
                      { label: "Updated At (Desc)", value: "updatedAt:-1" },
                      { label: "Updated At (Asc)", value: "updatedAt:1" },
                    ]}
                    getOptionLabel={(opt) => opt.label}
                    getOptionValue={(opt) => opt.value}
                  />
                </TableFilter>
                <Tooltip title="Download Excel">
                  <span>
                    <IconButton
                      onClick={handleDownloadExcel}
                      color="primary"
                      disabled={
                        isLoading ||
                        isFetching ||
                        isDownloading ||
                        !codRes?.data?.length
                      }
                    >
                      <Download />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            {isLoading || isFetching || loadingVendors ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : codRes?.data?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No data available for the selected filters.
                </Typography>
              </Box>
            ) : (
              <>
                <AppTable
                  columns={columns}
                  data={codRes?.data ?? []}
                  progressPending={isLoading || isFetching}
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={codRes?.totalItems ?? 0}
                  paginationDefaultPage={currentPage}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                />
              </>
            )}
          </Box>
        </BreadcrumbLayout.Paper>
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </BreadcrumbLayout>
    </LocalizationProvider>
  );
};

export default CODReport;
